import { Button } from "@/components/ui/button"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/context/AuthContext"
import { createCertificateRequest, type CertificateType } from "@/lib/collections/certificateRequests/user/addCertificateRequest"
// import { generateAndUploadCertificate } from "@/lib/storage/standar_employment_certificate/addFileStorage"
import { DiamondPlus } from "lucide-react"
import { useState } from "react"
import { toast } from "react-toastify"
import { sendCertificateToN8n } from "../../webhook/sendCertificateToN8n"


const CERTIFICATE_TYPES = [
    { value: 'standard', label: 'Estandar' },
    { value: 'with_salary', label: 'Con salario' },
    { value: 'job_functions', label: 'Con funciones' },
] as const;

const AddCertificateRequest = ({ onSuccess }: { onSuccess?: () => void }) => {
    const [reason, setReason] = useState("");
    const [type, setType] = useState<CertificateType | "">("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { user } = useAuth(); // Asume que tienes el userId aquí
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleSubmit = async () => {
        if (!type || !reason.trim()) {
            toast.warning("Por favor completa todos los campos");
            return;
        }

        if (!user?.$id) {
            toast.warning("No se encontró el usuario");
            return;
        }

        const selectedType = type as CertificateType;
        setIsSubmitting(true);

        try {
            // 1. Crear la solicitud
            const result = await createCertificateRequest({
                userId: user.$id,
                reason: reason.trim(),
                type: selectedType
            });


            if (!result.success) {
                toast.error("Error al crear la solicitud");
                return;
            }

            const requestId = result.data?.$id;

            if (requestId) {
                const toastId = toast.loading("Espere un momento por favor...");

                const webhookResult = await sendCertificateToN8n(
                    requestId,
                    user.$id,
                    user.name,
                    selectedType,
                    user.email
                );

                toast.dismiss(toastId);

                if (webhookResult.success) {
                    toast.success("Solicitud enviada exitosamente");
                } else {
                    toast.warning(`Solicitud creada pero error al notificar: ${webhookResult.error}`);
                }
            }

            setReason("");
            setType("");
            setIsDialogOpen(false);
            onSuccess?.();  // Esta línea refresca la lista

        } catch (error: any) {
            console.error("Error en handleSubmit:", error);
            toast.error(`Error: ${error.message || 'Error desconocido'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer bg-sky-500 dark:text-white hover:bg-sky-600"> <DiamondPlus />Registrar petición</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-106.25 font-outfit max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Solicitud de certificado</DialogTitle>
                    <DialogDescription>
                        Aquí podras realizar una solicitud para obtener tu certificado.
                    </DialogDescription>
                </DialogHeader>


                <div className="grid gap-4">

                    <div className="grid gap-3">
                        <Label>Selecciona el tipo de certificado:</Label>
                        <select
                            name="type"
                            id="type"
                            value={type}
                            onChange={(e) => setType(e.target.value as CertificateType)}
                            className="border rounded-lg dark:bg-black w-full h-9 px-2"
                        >

                            <option value="" disabled>--Selecciona una opción--</option>
                            {CERTIFICATE_TYPES.map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid gap-3">
                        <Label htmlFor="description">Motivo:</Label>
                        <Textarea
                            id="reason"
                            name="reason"
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            placeholder="Ingrese la razón por la cual deseas obtener el certificado.."
                        />
                    </div>
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            className="cursor-pointer"
                            variant="outline"
                        // disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? "Enviando..." : "Enviar solicitud"}
                    </Button>
                </DialogFooter>
            </DialogContent>

        </Dialog >
    )
}

export default AddCertificateRequest;
